import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { X, Check } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "../lib/api";

const ease = [0.22, 1, 0.36, 1];

function Reveal({ children, delay = 0 }) {
  return (
    <motion.div initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.8, ease, delay }}>
      {children}
    </motion.div>
  );
}

export default function Pricing() {
  const [config, setConfig] = useState(null);
  const [active, setActive] = useState(null); // package selected for checkout

  useEffect(() => {
    api.get("/payments/packages").then((r) => setConfig(r.data)).catch(() => {});
  }, []);

  if (!config) return null;

  const featured = config.packages.find((p) => p.featured);
  const base = config.packages.filter((p) => !p.featured);
  const savings = featured ? Math.round(parseFloat(featured.regular) - parseFloat(featured.amount)) : 0;

  return (
    <section id="pricing" className="max-w-7xl mx-auto px-6 py-28">
      <Reveal>
        <div className="grid md:grid-cols-12 gap-8 items-end mb-16">
          <div className="md:col-span-8">
            <div className="overline text-gold mb-5">Transparent pricing</div>
            <h2 className="serif font-light text-4xl sm:text-5xl lg:text-6xl text-bone tracking-tighter leading-tight">Fixed fees. No surprises.</h2>
          </div>
          <p className="md:col-span-4 text-bone/50 leading-relaxed">Pay securely online with PayPal or any major card. Every package includes a personal review with our team.</p>
        </div>
      </Reveal>

      {featured && (
        <Reveal>
          <div data-testid={`package-${featured.id}`} className="relative border border-gold bg-gradient-to-br from-gold/[0.08] to-transparent p-8 sm:p-10 mb-8 grid lg:grid-cols-12 gap-8 items-center">
            <span className="absolute -top-3 left-8 bg-gold text-ink overline px-4 py-1.5">Best value · Save ${savings}</span>
            <div className="lg:col-span-7">
              <div className="serif text-3xl sm:text-4xl text-bone mb-3">{featured.name}</div>
              <p className="text-bone/60 leading-relaxed mb-5 max-w-xl">{featured.desc}</p>
              <div className="flex flex-wrap gap-x-6 gap-y-2">
                {(featured.includes || []).map((inc) => (
                  <span key={inc} className="inline-flex items-center gap-2 text-bone/70 text-sm"><Check size={16} weight="bold" className="text-gold" /> {inc}</span>
                ))}
              </div>
            </div>
            <div className="lg:col-span-5 lg:text-right">
              <div className="flex items-end lg:justify-end gap-3 mb-1">
                <span className="serif text-6xl text-bone">${Math.round(parseFloat(featured.amount))}</span>
                <span className="serif text-2xl text-bone/30 line-through mb-2">${Math.round(parseFloat(featured.regular))}</span>
              </div>
              <div className="text-bone/30 text-xs mb-6">{config.currency} · one-time</div>
              <button data-testid={`buy-${featured.id}`} onClick={() => setActive(featured)} className="w-full lg:w-auto px-10 py-4 bg-gold text-ink overline hover:bg-bone transition-colors duration-300">Get Complete Protection</button>
            </div>
          </div>
        </Reveal>
      )}

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-border/50">
        {base.map((p, i) => {
          const inBundle = featured && ["will", "trust", "poa"].includes(p.id);
          return (
          <Reveal key={p.id} delay={i * 0.06}>
            <div data-testid={`package-${p.id}`} className="group bg-ink p-8 h-full flex flex-col hover:bg-[#0f0f0f] transition-colors duration-500">
              <div className="overline text-gold mb-6">{p.name}</div>
              <div className="serif text-5xl text-bone mb-1">${Math.round(parseFloat(p.amount))}</div>
              <div className="text-bone/30 text-xs mb-6">{config.currency} · one-time</div>
              <p className="text-bone/50 text-sm leading-relaxed flex-1 mb-6">{p.desc}</p>
              <button
                data-testid={`buy-${p.id}`}
                onClick={() => setActive(p)}
                className="w-full py-3.5 border border-gold text-gold overline hover:bg-gold hover:text-ink transition-colors duration-300"
              >
                Select &amp; Pay
              </button>
              {inBundle && (
                <button
                  data-testid={`upsell-${p.id}`}
                  onClick={() => setActive(featured)}
                  className="mt-3 w-full text-center text-bone/40 text-xs hover:text-gold transition-colors duration-300"
                >
                  Or save ${savings} with the Complete Bundle →
                </button>
              )}
            </div>
          </Reveal>
        );})}
      </div>

      <AnimatePresence>
        {active && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] flex items-center justify-center p-6 bg-ink/80 backdrop-blur-sm"
            onClick={() => setActive(null)}
            data-testid="checkout-overlay"
          >
            <motion.div
              initial={{ opacity: 0, y: 30, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 30, scale: 0.96 }}
              transition={{ duration: 0.35, ease }}
              className="w-full max-w-md bg-[#0e0e0e] border border-border rounded-lg p-8"
              onClick={(e) => e.stopPropagation()}
              data-testid="checkout-modal"
            >
              <div className="flex items-start justify-between mb-6">
                <div>
                  <div className="overline text-gold mb-1">{active.name}</div>
                  <div className="serif text-4xl text-bone">${Math.round(parseFloat(active.amount))} <span className="text-bone/30 text-lg">{config.currency}</span></div>
                </div>
                <button onClick={() => setActive(null)} data-testid="checkout-close" className="text-bone/50 hover:text-bone transition-colors duration-300"><X size={24} /></button>
              </div>
              <p className="text-bone/50 text-sm leading-relaxed mb-8">{active.desc}</p>

              {!config.configured ? (
                <div className="border border-gold/30 bg-gold/5 p-5 text-bone/70 text-sm leading-relaxed" data-testid="paypal-not-configured">
                  <Check size={18} className="text-gold inline mr-2" />
                  Checkout is ready — add your PayPal Client ID &amp; Secret on the backend to accept live payments.
                </div>
              ) : (
                <div data-testid="paypal-buttons">
                  <PayPalScriptProvider options={{ clientId: config.client_id, currency: config.currency, intent: "capture" }}>
                    <PayPalButtons
                      style={{ layout: "vertical", color: "gold", shape: "sharp", label: "pay" }}
                      createOrder={async () => {
                        const { data } = await api.post("/payments/create-order", { package_id: active.id });
                        return data.order_id;
                      }}
                      onApprove={async (data) => {
                        try {
                          const { data: res } = await api.post("/payments/capture-order", { order_id: data.orderID });
                          if (res.status === "COMPLETED") {
                            toast.success(`Payment received — ${res.package_name}. Thank you!`);
                            setActive(null);
                          } else {
                            toast.error("Payment not completed. Please try again.");
                          }
                        } catch {
                          toast.error("We couldn't confirm your payment. Please contact us.");
                        }
                      }}
                      onError={() => toast.error("PayPal encountered an error. Please try again.")}
                    />
                  </PayPalScriptProvider>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
